import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';

type Kind = 'habit' | 'task';
type Freq = 'daily' | 'weekly' | 'once';
type Day = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export const ENERGY_VALUES = [1, 2, 3, 5, 8, 13, 21] as const;

const DAY_ORDER: Day[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABEL: Record<Day, string> = {
  mon: 'Pzt', tue: 'Sal', wed: 'Çar', thu: 'Per', fri: 'Cum', sat: 'Cmt', sun: 'Paz',
};

interface Existing {
  id: string;
  title: string;
  energy: number;
  scheduleRule: string;
}

interface Props {
  kind: Kind;
  existing?: Existing;
  onClose: () => void;
}

function parseRule(rule: string): { freq: Freq; days: Day[]; time: string } {
  const s = rule.trim().toLowerCase();
  if (s === 'once') return { freq: 'once', days: [], time: '09:00' };
  const d = s.match(/^daily\s+(\d{1,2}):(\d{2})$/);
  if (d) return { freq: 'daily', days: [], time: `${d[1]!.padStart(2, '0')}:${d[2]}` };
  const w = s.match(/^weekly\s+([a-z,]+)\s+(\d{1,2}):(\d{2})$/);
  if (w) {
    return {
      freq: 'weekly',
      days: w[1]!.split(',').map((x) => x.trim() as Day),
      time: `${w[2]!.padStart(2, '0')}:${w[3]}`,
    };
  }
  return { freq: 'daily', days: [], time: '09:00' };
}

function buildRule(freq: Freq, days: Day[], time: string): string {
  if (freq === 'once') return 'once';
  if (freq === 'daily') return `daily ${time}`;
  const ordered = DAY_ORDER.filter((d) => days.includes(d));
  return `weekly ${ordered.join(',')} ${time}`;
}

export function TemplateModal({ kind, existing, onClose }: Props) {
  const qc = useQueryClient();
  const initial = useMemo(
    () => existing ? parseRule(existing.scheduleRule) : parseRule(kind === 'habit' ? 'daily 08:00' : 'once'),
    [existing, kind],
  );

  const [title, setTitle] = useState(existing?.title ?? '');
  const [energy, setEnergy] = useState<number>(existing?.energy ?? 2);
  const [freq, setFreq] = useState<Freq>(initial.freq);
  const [days, setDays] = useState<Day[]>(initial.days);
  const [time, setTime] = useState<string>(initial.time);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = useMutation({
    mutationFn: async () => {
      const scheduleRule = buildRule(freq, days, time);
      if (existing) {
        return api(`/api/tasks/templates/${existing.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ title, energy, scheduleRule }),
        });
      }
      return api('/api/tasks/templates', {
        method: 'POST',
        body: JSON.stringify({ title, energy, scheduleRule }),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['today'] });
      qc.invalidateQueries({ queryKey: ['templates'] });
      onClose();
    },
    onError: (e) => setErr(e instanceof Error ? e.message : 'Hata'),
  });

  const heading = existing
    ? (kind === 'habit' ? 'Alışkanlığı düzenle' : 'Görevi düzenle')
    : (kind === 'habit' ? 'Yeni Alışkanlık' : 'Yeni Günlük Görev');

  // A habit defaults to daily, a task defaults to once — but you can change both.
  function toggleDay(d: Day) {
    setDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!title.trim()) return setErr('Başlık gerekli');
    if (freq === 'weekly' && days.length === 0) return setErr('En az bir gün seç');
    save.mutate();
  }

  return (
    <div
      className="fixed inset-0 z-30 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md space-y-5 rounded-2xl border border-border-soft bg-bg-raised p-6 shadow-2xl"
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-neutral-100">{heading}</h3>
          <button type="button" onClick={onClose}
                  className="rounded px-2 text-xl text-neutral-500 hover:text-neutral-200">×</button>
        </div>

        <Field label="Başlık">
          <input
            value={title}
            autoFocus
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Örn: 30 dakika koş"
            className="w-full rounded-md bg-bg-elevated px-3 py-2 text-neutral-100 outline-none focus:ring-2 focus:ring-brand-500"
          />
        </Field>

        <Field label="Enerji" hint="Göreli efor — Fibonacci (sprint point)">
          <div className="flex flex-wrap gap-2">
            {ENERGY_VALUES.map((v) => (
              <button key={v} type="button"
                      onClick={() => setEnergy(v)}
                      className={`rounded-md border px-3 py-1.5 font-pixel text-xs transition ${
                        energy === v
                          ? 'border-brand-400 bg-brand-500/20 text-brand-300'
                          : 'border-border-soft/60 text-neutral-400 hover:border-brand-500/60'
                      }`}>
                ⚡ {v}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Sıklık">
          <select value={freq} onChange={(e) => setFreq(e.target.value as Freq)}
                  className="w-full rounded-md bg-bg-elevated px-3 py-2 text-neutral-100">
            <option value="daily">Her gün</option>
            <option value="weekly">Haftanın belirli günleri</option>
            <option value="once">Tek seferlik</option>
          </select>
        </Field>

        {freq === 'weekly' && (
          <Field label="Günler">
            <div className="flex flex-wrap gap-2">
              {DAY_ORDER.map((d) => (
                <button key={d} type="button"
                        onClick={() => toggleDay(d)}
                        className={`w-11 rounded-md border py-1.5 text-xs transition ${
                          days.includes(d)
                            ? 'border-brand-400 bg-brand-500/20 text-brand-300'
                            : 'border-border-soft/60 text-neutral-400 hover:border-brand-500/60'
                        }`}>
                  {DAY_LABEL[d]}
                </button>
              ))}
            </div>
          </Field>
        )}

        {freq !== 'once' && (
          <Field label="Saat">
            <input type="time" value={time}
                   onChange={(e) => setTime(e.target.value)}
                   className="rounded-md bg-bg-elevated px-3 py-2 text-neutral-100" />
          </Field>
        )}

        {err && <p className="text-sm text-red-400">{err}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose}
                  className="rounded-md px-4 py-2 text-sm text-neutral-400 hover:text-neutral-100">
            İptal
          </button>
          <button type="submit" disabled={save.isPending}
                  className="rounded-md bg-brand-500 px-5 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-50">
            {existing ? 'Kaydet' : 'Ekle'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between">
        <label className="text-xs font-medium uppercase tracking-wider text-neutral-400">{label}</label>
        {hint && <span className="text-[10px] text-neutral-500">{hint}</span>}
      </div>
      {children}
    </div>
  );
}
